/* eslint-disable @typescript-eslint/no-redeclare */
import type {
  DestinationChannelType as GeneratedDestinationChannelType,
  DestinationCoverageStatus,
  DestinationEvidenceSourceType,
  DestinationOption as GeneratedDestinationOption,
  DestinationResearchStatus,
  DestinationSaveMode,
  DestinationVerificationStatus,
  PhoneOption as GeneratedPhoneOption,
  ProviderDestinationScope,
  ProviderServiceDestinationsScopeBlock,
} from '@/services/api/generated/providerDestinationsService';
import type {
  ResendReason as GeneratedResendReason,
  ResendTreatmentRecordRequestSchema,
  TreatmentRecordTypes,
} from '@/services/api/generated/treatmentRecordRequestsService';

// ---------------------------------------------------------------------------
// API-backed destination enums (runtime constants + generated type aliases)
// ---------------------------------------------------------------------------

export type DestinationScope = ProviderDestinationScope;
export const DestinationScope = {
  RECORDS: 'RECORDS',
  BILLS: 'BILLS',
  RADIOLOGY: 'RADIOLOGY',
  ER_PHYSICIAN: 'ER_PHYSICIAN',
} as const satisfies Record<string, DestinationScope>;

export type VerificationStatus = DestinationVerificationStatus;
export const VerificationStatus = {
  VERIFIED: 'VERIFIED',
  PENDING: 'PENDING',
  UNVERIFIED: 'UNVERIFIED',
  DISPUTED: 'DISPUTED',
  ARCHIVED: 'ARCHIVED',
} as const satisfies Record<string, VerificationStatus>;

export type DestinationChannelType = GeneratedDestinationChannelType;
export const DestinationChannelType = {
  FAX: 'FAX',
  EMAIL: 'EMAIL',
  MAIL: 'MAIL',
  PORTAL: 'PORTAL',
} as const satisfies Record<string, DestinationChannelType>;

export type SaveMode = DestinationSaveMode;
export const SaveMode = {
  SAVE_FOR_FUTURE: 'SAVE_FOR_FUTURE',
  USE_ONCE: 'USE_ONCE',
} as const satisfies Record<string, SaveMode>;

export type ResearchStatus = DestinationResearchStatus;
export const ResearchStatus = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  READY: 'READY',
  FAILED: 'FAILED',
} as const satisfies Record<string, ResearchStatus>;

export type CoverageStatus = DestinationCoverageStatus;

export type DestinationOption = GeneratedDestinationOption;
export type PhoneOption = GeneratedPhoneOption;
export type ScopeDestinationBlock = ProviderServiceDestinationsScopeBlock;

// ---------------------------------------------------------------------------
// Transition helpers
// ---------------------------------------------------------------------------

const LEGACY_TO_SCOPE: Partial<Record<TreatmentRecordTypes, DestinationScope>> = {
  'Facility Records': DestinationScope.RECORDS,
  'Facility Bills': DestinationScope.BILLS,
  Radiology: DestinationScope.RADIOLOGY,
  'ER Physician Bills': DestinationScope.ER_PHYSICIAN,
};

const SCOPE_TO_LEGACY: Record<DestinationScope, TreatmentRecordTypes> = {
  [DestinationScope.RECORDS]: 'Facility Records',
  [DestinationScope.BILLS]: 'Facility Bills',
  [DestinationScope.RADIOLOGY]: 'Radiology',
  [DestinationScope.ER_PHYSICIAN]: 'ER Physician Bills',
};

export const LEGACY_SCOPE_MAP = {
  ...LEGACY_TO_SCOPE,

  toScope(legacy: TreatmentRecordTypes): DestinationScope | undefined {
    return LEGACY_TO_SCOPE[legacy];
  },

  toLegacy(scope: DestinationScope): TreatmentRecordTypes | undefined {
    return SCOPE_TO_LEGACY[scope];
  },
} as const;

// ---------------------------------------------------------------------------
// Request template
// ---------------------------------------------------------------------------

export type RequestTemplateIntent = 'STANDARD' | 'HOSPITAL';
export const RequestTemplateIntent = {
  STANDARD: 'STANDARD',
  HOSPITAL: 'HOSPITAL',
} as const satisfies Record<string, RequestTemplateIntent>;

// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------

export type EvidenceSourceType = DestinationEvidenceSourceType;
export const EvidenceSourceType = {
  USER: 'USER',
  PARALLEL: 'PARALLEL',
  VOICEAI: 'VOICEAI',
  SYSTEM: 'SYSTEM',
} as const satisfies Record<string, EvidenceSourceType>;

export const ManualEvidenceType = {
  CALLED_PROVIDER: 'CALLED_PROVIDER',
  PROVIDER_WEBSITE: 'PROVIDER_WEBSITE',
  PREVIOUS_EXPERIENCE: 'PREVIOUS_EXPERIENCE',
  OTHER: 'OTHER',
} as const;
export type ManualEvidenceType =
  (typeof ManualEvidenceType)[keyof typeof ManualEvidenceType];

export const UI_MANUAL_EVIDENCE_TO_API_SOURCE: Record<
  ManualEvidenceType,
  EvidenceSourceType
> = {
  [ManualEvidenceType.CALLED_PROVIDER]: EvidenceSourceType.USER,
  [ManualEvidenceType.PROVIDER_WEBSITE]: EvidenceSourceType.USER,
  [ManualEvidenceType.PREVIOUS_EXPERIENCE]: EvidenceSourceType.USER,
  [ManualEvidenceType.OTHER]: EvidenceSourceType.USER,
};

// ---------------------------------------------------------------------------
// Resend
// ---------------------------------------------------------------------------

export type ResendReason = GeneratedResendReason;

export const ResendSameReason = {
  NO_RESPONSE_SLA_EXCEEDED: 'NO_RESPONSE_SLA_EXCEEDED',
  FAX_FAILED: 'FAX_FAILED',
  PROVIDER_SAID_NOT_RECEIVED: 'PROVIDER_SAID_NOT_RECEIVED',
  MISSING_DOCS_OR_CORRECTED_FORM: 'MISSING_DOCS_OR_CORRECTED_FORM',
  OTHER: 'OTHER',
} as const satisfies Record<string, ResendReason>;

export const DestinationChangeReason = {
  WRONG_NUMBER_OR_INBOX: 'WRONG_NUMBER_OR_INBOX',
  NOT_RIGHT_DEPARTMENT: 'NOT_RIGHT_DEPARTMENT',
  FAX_FAILED: 'FAX_FAILED',
  PROVIDER_REJECTED_DESTINATION: 'PROVIDER_REJECTED_DESTINATION',
  OTHER: 'OTHER',
} as const satisfies Record<string, ResendReason>;

export type ResendRequestPayload = ResendTreatmentRecordRequestSchema;
